import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import API from "../services/api";
import SeoHead from "../components/seo/SeoHead";
import Footer from "../components/Footer";

const THEME_KEY = "hookora_theme";

function ForgotPasswordPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      cardBg: isDark ? "rgba(15,23,42,0.82)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      link: isDark ? "#93c5fd" : "#2563eb",
      errorBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.35)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
      successBg: isDark ? "rgba(20,83,45,0.30)" : "#f0fdf4",
      successBorder: isDark ? "rgba(74,222,128,0.30)" : "#bbf7d0",
      successText: isDark ? "#bbf7d0" : "#166534",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 10px 30px rgba(15, 23, 42, 0.05)"
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "14px",
    marginBottom: "12px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    boxSizing: "border-box",
    fontSize: "15px",
    background: colors.inputBg,
    color: colors.inputText
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");
      setMessage("");

      const response = await API.post("/users/forgot-password", {
        email: email.trim()
      });

      setMessage(response.data.message);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to process request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: colors.pageBg, backgroundImage: colors.pageGradient, minHeight: "100vh" }}>
      <SeoHead
        title="Forgot Password | Hookora"
        description="Request a password reset link for your Hookora account."
        path="/forgot-password"
      />

      <Navbar />

      <div
        style={{
          maxWidth: "520px",
          margin: isMobile ? "24px auto" : "40px auto",
          padding: isMobile ? "14px" : "20px"
        }}
      >
        <div
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: isMobile ? "18px" : "20px",
            padding: isMobile ? "22px" : "28px",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none"
          }}
        >
          <h1 style={{ marginTop: 0, color: colors.text }}>Forgot password</h1>
          <p style={{ color: colors.textMuted, lineHeight: "1.6" }}>
            Enter your email address and we’ll generate a password reset link.
          </p>

          <form onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "14px",
                background: loading ? "#9ca3af" : "linear-gradient(135deg, #2563eb, #7c3aed)",
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: "700",
                fontSize: "15px"
              }}
            >
              {loading ? "Sending..." : "Send reset link"}
            </button>
          </form>

          {message && (
            <div
              style={{
                marginTop: "16px",
                color: colors.successText,
                background: colors.successBg,
                padding: "12px",
                borderRadius: "12px",
                border: `1px solid ${colors.successBorder}`,
                lineHeight: "1.5"
              }}
            >
              {message}
            </div>
          )}

          {error && (
            <div
              style={{
                marginTop: "16px",
                color: colors.errorText,
                background: colors.errorBg,
                padding: "12px",
                borderRadius: "12px",
                border: `1px solid ${colors.errorBorder}`,
                lineHeight: "1.5"
              }}
            >
              {error}
            </div>
          )}

          <p style={{ marginTop: "20px", color: colors.textMuted }}>
            Back to{" "}
            <Link to="/login" style={{ color: colors.link }}>
              Login
            </Link>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ForgotPasswordPage;